// ABOUTME: In-boutique call-to-action shown when the guest is inside without audio consent.
// ABOUTME: Makes a silent AI concierge self-explanatory and one tap from being enabled.

import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MicOff } from 'lucide-react';
import { DISCLOSURE } from './consentConfig';
import { MOBILE_UNIT, ICON_RAIL_MAX_BOTTOM } from '../components/ReelChrome/mobileGeometry';

/**
 * Where the prompt sits. `generic` (non-reel tenants) keeps the original bottom-centre spot. The reel chrome has no free bottom
 * band: the caption bar, the voice-first dock and its controls row, and the variant rail above them (which on a wider-than-16:9
 * window runs to the right edge) all live there. Desktop takes the top-right under the corner icons - right of the centred
 * collection pill (the width cap keeps it clear down to 1024px) and above the spec-sheet card (28% down); a phone takes the top,
 * below the always-expanded three-chip icon rail's full extent (ICON_RAIL_MAX_BOTTOM, mobileGeometry's own top-overlay rule).
 */
export const PLACEMENT = {
  generic: { wrap: 'fixed bottom-24 left-0 right-0 flex justify-center', style: undefined },
  'reel-desktop': { wrap: 'fixed flex justify-end', style: { right: '2vw', top: 'max(7rem, 12vh)', maxWidth: 'calc(50vw - 13rem)' } },
  'reel-mobile': {
    wrap: 'fixed left-0 right-0 flex justify-center px-3',
    style: { '--u': MOBILE_UNIT, top: `calc(${ICON_RAIL_MAX_BOTTOM} * var(--u) + 12px)` },
  },
};

/** The steps that lift a browser block, for the guest's own device (desktop browsers share one wording). */
export function hardBodyFor(ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '') {
  if (/iPhone|iPad|iPod/.test(ua)) return DISCLOSURE.micHardBodyIos;
  if (/Android/.test(ua)) return DISCLOSURE.micHardBodyAndroid;
  return DISCLOSURE.micHardBody;
}

/** Title, body and button for each reason the concierge cannot hear (absent `mic` = no voice consent, the original case). */
function copyFor(mic) {
  if (mic === 'hard') return [DISCLOSURE.micBlockedTitle, hardBodyFor(), null]; // the browser will not ask again: no button
  if (mic === 'unavailable') return [DISCLOSURE.micOffTitle, DISCLOSURE.micUnavailableBody, DISCLOSURE.retryMicLabel];
  if (mic === 'failed') return [DISCLOSURE.micOffTitle, DISCLOSURE.micFailedBody, DISCLOSURE.retryMicLabel];
  if (mic === 'voice') return [DISCLOSURE.voiceOffTitle, DISCLOSURE.turnOnVoiceBody, DISCLOSURE.turnOnVoiceLabel];
  if (mic === 'start') return [DISCLOSURE.startTitle, DISCLOSURE.startBody, DISCLOSURE.startLabel];
  if (mic === 'denied') return [DISCLOSURE.micOffTitle, DISCLOSURE.micOffBody, DISCLOSURE.enableMicLabel];
  return [DISCLOSURE.voiceOffTitle, DISCLOSURE.voiceOffBody, DISCLOSURE.enableVoiceLabel];
}

/**
 * @param {object} p
 * @param {boolean} p.show
 * @param {Function} p.onEnable
 * @param {'generic'|'reel-desktop'|'reel-mobile'} [p.placement] see PLACEMENT
 * @param {null|'start'|'denied'|'hard'|'unavailable'|'failed'} [p.mic] consent is given but the mic is not on: not started yet (`start`, the
 *   no_enter_gate return landing) or refused by the browser (useMicBlocked)
 */
export function VoiceOffPrompt({ show, onEnable, mic = null, placement = 'generic' }) {
  const [title, body, label] = copyFor(mic);
  const place = PLACEMENT[placement] || PLACEMENT.generic;
  return (
    <AnimatePresence>
      {show && (
        // The wrapper places, the pill animates: framer-motion writes `transform` for `y`, which used to wipe the pill's own
        // -translate-x-1/2 and leave it starting at the centre line instead of centred on it.
        <div key="voice-off-prompt" className={`${place.wrap} z-[115] pointer-events-none`} style={place.style}>
        <motion.div
          data-voice-off={mic || 'consent'}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          className="flex items-center gap-3 px-5 py-3 bg-black/90 text-white backdrop-blur rounded-full border border-white/10 shadow-2xl pointer-events-auto font-sans"
        >
          <MicOff size={16} className="shrink-0 text-white/80" />
          <span className="flex flex-col leading-tight pr-1">
            <span className="text-xs font-medium tracking-wide">{title}</span>
            <span className="text-[11px] text-white/60">{body}</span>
          </span>
          {label && <button
            type="button"
            onClick={onEnable}
            className="ml-1 px-4 py-2 rounded-full bg-white text-black hover:bg-neutral-200 transition-colors tracking-widest uppercase text-[10px] font-semibold whitespace-nowrap"
          >
            {label}
          </button>}
        </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
